/**
 * Autore: Salvo de Luca
 * Azienda Dialogicom s.r.l.s.
 * File: src/components/forms/utils/formatters.ts
 * Release: v1.0.0
 * Data: 28/05/2025
 */

/**
 * Converte in maiuscolo la prima lettera di ogni parola
 * @param value Testo da formattare
 * @returns Testo formattato con iniziali maiuscole
 */
export function formatCapitalizeWords(value: string): string {
  if (!value) return '';
  
  return value
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Converte l'intero testo in maiuscolo
 * @param value Testo da formattare
 * @returns Testo formattato in maiuscolo
 */
export function formatUppercase(value: string): string {
  if (!value) return '';
  
  return value.toUpperCase();
}

/**
 * Formatta un codice fiscale (rimuove spazi e converte in maiuscolo)
 * @param value Codice fiscale da formattare
 * @returns Codice fiscale formattato
 */
export function formatCodiceFiscale(value: string): string {
  if (!value) return '';
  
  return value.replace(/\s/g, '').toUpperCase();
}

/**
 * Formatta una partita IVA (rimuove caratteri non numerici)
 * @param value Partita IVA da formattare
 * @returns Partita IVA formattata
 */
export function formatPartitaIva(value: string): string {
  if (!value) return '';
  
  return value.replace(/\D/g, '');
}

/**
 * Formatta un codice univoco (rimuove spazi e converte in maiuscolo)
 * @param value Codice univoco da formattare
 * @returns Codice univoco formattato
 */
export function formatCodiceUnivoco(value: string): string {
  if (!value) return '';
  
  return value.replace(/\s/g, '').toUpperCase();
}

/**
 * Formatta un numero di telefono (mantiene solo numeri e il prefisso +)
 * @param value Numero di telefono da formattare
 * @returns Numero di telefono formattato
 */
export function formatPhoneNumber(value: string): string {
  if (!value) return '';
  
  // Conserva solo il + iniziale e i numeri
  return value.replace(/[^\d+]/g, '');
}

/**
 * Formatta un CAP (mantiene solo numeri, massimo 5)
 * @param value CAP da formattare
 * @returns CAP formattato
 */
export function formatZip(value: string): string {
  if (!value) return '';
  
  const digitsOnly = value.replace(/\D/g, '');
  return digitsOnly.substring(0, 5);
}

/**
 * Formatta una provincia (converte in maiuscolo, massimo 2 caratteri)
 * @param value Provincia da formattare
 * @returns Provincia formattata
 */
export function formatProvince(value: string): string {
  if (!value) return '';
  
  return value.toUpperCase().substring(0, 2);
}

/**
 * Raggruppo i formattatori in un oggetto per facilità d'uso
 */
export const formatters = {
  capitalizeWords: formatCapitalizeWords,
  uppercase: formatUppercase,
  toUpperCase: formatUppercase,
  codiceFiscale: formatCodiceFiscale,
  partitaIva: formatPartitaIva,
  codiceUnivoco: formatCodiceUnivoco,
  phoneNumber: formatPhoneNumber,
  zip: formatZip,
  province: formatProvince
}; 